// src/redux/slices/connectionSlice.ts

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

// ==========================
// TYPES
// ==========================
type IdValue = number | string;

interface FetchAllUsersPayload {
  user_id: IdValue;
  search?: string;
  limit?: number;
  offset?: number;
}

interface SendConnectionPayload {
  sender_user_id: IdValue;
  receiver_user_id: IdValue;
}

interface ConnectionActionPayload {
  connection_id: IdValue;
}

interface RemoveRequestPayload {
  connection_id?: IdValue;
  sender_user_id?: IdValue;
  receiver_user_id?: IdValue;
}

interface RemoveConnectionPayload {
  connection_id?: IdValue;
  user_id?: IdValue;
  connected_user_id?: IdValue;
}

interface BlockPayload {
  blocker_user_id: IdValue;
  blocked_user_id: IdValue;
}

interface StatusPayload {
  logged_user_id: IdValue;
  profile_user_id: IdValue;
}

interface ProfileDetailsPayload {
  viewer_user_id: IdValue;
  profile_user_id: IdValue;
}

interface ConnectionState {
  users: any[];
  myConnections: any[];
  pendingRequests: any[];
  sentRequests: any[];
  connectionStatus: any | null;
  counts: any | null;

  profileLoading: boolean;
  selectedProfile: any | null;

  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  message: string | null;

  search: string;
  limit: number;
  offset: number;
}

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || error?.message || fallback;
};

// ==========================
// GET ALL USERS NOT CONNECTED
// ==========================
export const fetchAllUsers = createAsyncThunk(
  "connections/fetchAllUsers",
  async (payload: FetchAllUsersPayload, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/connections/get-all-users`, {
        params: {
          user_id: payload.user_id,
          search: payload.search || "",
          limit: payload.limit || 20,
          offset: payload.offset || 0,
        },
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch users"));
    }
  }
);

// ==========================
// SEND CONNECTION REQUEST
// ==========================
export const sendConnectionRequest = createAsyncThunk(
  "connections/sendConnectionRequest",
  async (payload: SendConnectionPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("sender_user_id", String(payload.sender_user_id));
      formData.append("receiver_user_id", String(payload.receiver_user_id));

      const res = await axios.post(
        `${API_URL}/connections/send-request`,
        formData
      );

      return {
        ...res.data,
        sender_user_id: payload.sender_user_id,
        receiver_user_id: payload.receiver_user_id,
      };
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, "Failed to send request"));
    }
  }
);

// ==========================
// ACCEPT CONNECTION REQUEST
// ==========================
export const acceptConnectionRequest = createAsyncThunk(
  "connections/acceptConnectionRequest",
  async (payload: ConnectionActionPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("connection_id", String(payload.connection_id));

      const res = await axios.post(
        `${API_URL}/connections/accept-request`,
        formData
      );

      return {
        ...res.data,
        connection_id: payload.connection_id,
      };
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to accept request")
      );
    }
  }
);

// ==========================
// REJECT CONNECTION REQUEST
// ==========================
export const rejectConnectionRequest = createAsyncThunk(
  "connections/rejectConnectionRequest",
  async (payload: ConnectionActionPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("connection_id", String(payload.connection_id));

      const res = await axios.post(
        `${API_URL}/connections/reject-request`,
        formData
      );

      return {
        ...res.data,
        connection_id: payload.connection_id,
      };
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to reject request")
      );
    }
  }
);

// ==========================
// REMOVE / CANCEL CONNECTION REQUEST
// Used for sent invitations
// POST /connections/remove-request
// Payload can be:
// connection_id
// OR sender_user_id + receiver_user_id
// ==========================
export const removeConnectionRequest = createAsyncThunk(
  "connections/removeConnectionRequest",
  async (payload: RemoveRequestPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      if (payload.connection_id) {
        formData.append("connection_id", String(payload.connection_id));
      } else {
        formData.append("sender_user_id", String(payload.sender_user_id || ""));
        formData.append(
          "receiver_user_id",
          String(payload.receiver_user_id || "")
        );
      }

      const res = await axios.post(
        `${API_URL}/connections/remove-request`,
        formData
      );

      return {
        ...res.data,
        connection_id: payload.connection_id,
        sender_user_id: payload.sender_user_id,
        receiver_user_id: payload.receiver_user_id,
      };
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to remove connection request")
      );
    }
  }
);

// ==========================
// REMOVE ACCEPTED CONNECTION
// POST /connections/remove-connection
// Payload can be:
// connection_id
// OR user_id + connected_user_id
// ==========================
export const removeAcceptedConnection = createAsyncThunk(
  "connections/removeAcceptedConnection",
  async (payload: RemoveConnectionPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      if (payload.connection_id) {
        formData.append("connection_id", String(payload.connection_id));
      } else {
        formData.append("user_id", String(payload.user_id || ""));
        formData.append(
          "connected_user_id",
          String(payload.connected_user_id || "")
        );
      }

      const res = await axios.post(
        `${API_URL}/connections/remove-connection`,
        formData
      );

      return {
        ...res.data,
        connection_id: payload.connection_id,
        user_id: payload.user_id,
        connected_user_id: payload.connected_user_id,
      };
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to remove connection")
      );
    }
  }
);

// ==========================
// BLOCK USER
// ==========================
export const blockConnection = createAsyncThunk(
  "connections/blockConnection",
  async (payload: BlockPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("blocker_user_id", String(payload.blocker_user_id));
      formData.append("blocked_user_id", String(payload.blocked_user_id));

      const res = await axios.post(`${API_URL}/connections/block`, formData);

      return {
        ...res.data,
        blocked_user_id: payload.blocked_user_id,
      };
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, "Failed to block user"));
    }
  }
);

// ==========================
// UNBLOCK USER
// ==========================
export const unblockConnection = createAsyncThunk(
  "connections/unblockConnection",
  async (payload: BlockPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("blocker_user_id", String(payload.blocker_user_id));
      formData.append("blocked_user_id", String(payload.blocked_user_id));

      const res = await axios.post(`${API_URL}/connections/unblock`, formData);

      return {
        ...res.data,
        blocked_user_id: payload.blocked_user_id,
      };
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, "Failed to unblock user"));
    }
  }
);

// ==========================
// MY CONNECTIONS
// ==========================
export const fetchMyConnections = createAsyncThunk(
  "connections/fetchMyConnections",
  async (user_id: IdValue, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/connections/my-connections`, {
        params: { user_id },
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch connections")
      );
    }
  }
);

// ==========================
// PENDING REQUESTS RECEIVED
// ==========================
export const fetchPendingRequests = createAsyncThunk(
  "connections/fetchPendingRequests",
  async (user_id: IdValue, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/connections/pending`, {
        params: { user_id },
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch pending requests")
      );
    }
  }
);

// ==========================
// SENT REQUESTS / INVITATIONS SENT
// GET /connections/sent?user_id=36
// ==========================
export const fetchSentRequests = createAsyncThunk(
  "connections/fetchSentRequests",
  async (user_id: IdValue, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/connections/sent`, {
        params: { user_id },
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch sent requests")
      );
    }
  }
);

// ==========================
// CONNECTION STATUS
// ==========================
export const fetchConnectionStatus = createAsyncThunk(
  "connections/fetchConnectionStatus",
  async (payload: StatusPayload, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/connections/status`, {
        params: {
          logged_user_id: payload.logged_user_id,
          profile_user_id: payload.profile_user_id,
        },
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch connection status")
      );
    }
  }
);

// ==========================
// CONNECTED USER DETAILS
// viewer_user_id = logged in user
// profile_user_id = selected connection user
// ==========================
export const fetchConnectedUserDetails = createAsyncThunk(
  "connections/fetchConnectedUserDetails",
  async (payload: ProfileDetailsPayload, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/connections/profile-details`, {
        params: {
          viewer_user_id: payload.viewer_user_id,
          profile_user_id: payload.profile_user_id,
        },
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || {
          success: false,
          message: "Failed to fetch profile details",
          data: null,
          error: error?.message || null,
        }
      );
    }
  }
);

// ==========================
// NETWORK COUNTS
// ==========================
export const fetchUserNetworkCounts = createAsyncThunk(
  "connections/fetchUserNetworkCounts",
  async (user_id: IdValue, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/connections/counts`, {
        params: { user_id },
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch counts"));
    }
  }
);

const initialState: ConnectionState = {
  users: [],
  myConnections: [],
  pendingRequests: [],
  sentRequests: [],
  connectionStatus: null,
  counts: null,

  profileLoading: false,
  selectedProfile: null,

  loading: false,
  actionLoading: false,
  error: null,
  message: null,

  search: "",
  limit: 20,
  offset: 0,
};

const connectionSlice = createSlice({
  name: "connections",
  initialState,
  reducers: {
    resetConnectionError: (state) => {
      state.error = null;
    },

    resetConnectionMessage: (state) => {
      state.message = null;
    },

    setConnectionSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.offset = 0;
    },

    setConnectionPagination: (
      state,
      action: PayloadAction<{ limit: number; offset: number }>
    ) => {
      state.limit = action.payload.limit;
      state.offset = action.payload.offset;
    },

    clearConnectionStatus: (state) => {
      state.connectionStatus = null;
    },

    resetSelectedProfile: (state) => {
      state.selectedProfile = null;
      state.profileLoading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // ==========================
      // FETCH ALL USERS
      // ==========================
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload?.data || [];
        state.message = action.payload?.message || null;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.users = [];
        state.error = action.payload as string;
      })

      // ==========================
      // SEND REQUEST
      // ==========================
      .addCase(sendConnectionRequest.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(sendConnectionRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.message = action.payload?.message || "Request sent successfully";

        const receiverId = Number(action.payload?.receiver_user_id);

        state.users = state.users.map((user) =>
          Number(user.user_id) === receiverId
            ? {
                ...user,
                connection_status: "pending",
                button_text: "Request Sent",
              }
            : user
        );

        state.users = state.users.filter(
          (user) => Number(user.user_id) !== receiverId
        );
      })
      .addCase(sendConnectionRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ==========================
      // ACCEPT REQUEST
      // ==========================
      .addCase(acceptConnectionRequest.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(acceptConnectionRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.message = action.payload?.message || "Request accepted";

        const connectionId = Number(action.payload?.connection_id);

        state.pendingRequests = state.pendingRequests.filter(
          (item) => Number(item.connection_id) !== connectionId
        );
      })
      .addCase(acceptConnectionRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ==========================
      // REJECT REQUEST
      // ==========================
      .addCase(rejectConnectionRequest.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(rejectConnectionRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.message = action.payload?.message || "Request rejected";

        const connectionId = Number(action.payload?.connection_id);

        state.pendingRequests = state.pendingRequests.filter(
          (item) => Number(item.connection_id) !== connectionId
        );
      })
      .addCase(rejectConnectionRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ==========================
      // REMOVE / CANCEL REQUEST
      // ==========================
      .addCase(removeConnectionRequest.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(removeConnectionRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.message =
          action.payload?.message || "Connection request removed successfully";

        const connectionId = Number(
          action.payload?.data?.connection_id || action.payload?.connection_id
        );

        const senderId = Number(
          action.payload?.data?.sender_user_id || action.payload?.sender_user_id
        );

        const receiverId = Number(
          action.payload?.data?.receiver_user_id ||
            action.payload?.receiver_user_id
        );

        if (connectionId) {
          state.sentRequests = state.sentRequests.filter(
            (item) => Number(item.connection_id) !== connectionId
          );

          state.pendingRequests = state.pendingRequests.filter(
            (item) => Number(item.connection_id) !== connectionId
          );
        } else if (senderId && receiverId) {
          state.sentRequests = state.sentRequests.filter(
            (item) =>
              !(
                Number(item.sender_user_id) === senderId &&
                Number(item.receiver_user_id) === receiverId
              )
          );

          state.pendingRequests = state.pendingRequests.filter(
            (item) =>
              !(
                Number(item.sender_user_id) === senderId &&
                Number(item.receiver_user_id) === receiverId
              )
          );
        }
      })
      .addCase(removeConnectionRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ==========================
      // REMOVE ACCEPTED CONNECTION
      // ==========================
      .addCase(removeAcceptedConnection.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(removeAcceptedConnection.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.message =
          action.payload?.message || "Connection removed successfully";

        const connectionId = Number(
          action.payload?.data?.connection_id || action.payload?.connection_id
        );

        const connectedUserId = Number(
          action.payload?.data?.connected_user_id ||
            action.payload?.connected_user_id
        );

        if (connectionId) {
          state.myConnections = state.myConnections.filter(
            (item) => Number(item.connection_id) !== connectionId
          );
        } else if (connectedUserId) {
          state.myConnections = state.myConnections.filter(
            (item) =>
              Number(item.connected_user_id || item.user_id) !== connectedUserId
          );
        }

        if (
          connectionId &&
          Number(state.selectedProfile?.connection_id) === connectionId
        ) {
          state.selectedProfile = null;
        }
      })
      .addCase(removeAcceptedConnection.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ==========================
      // BLOCK USER
      // ==========================
      .addCase(blockConnection.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(blockConnection.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.message = action.payload?.message || "User blocked";

        const blockedUserId = Number(action.payload?.blocked_user_id);

        state.users = state.users.filter(
          (user) => Number(user.user_id) !== blockedUserId
        );

        state.myConnections = state.myConnections.filter(
          (user) =>
            Number(user.user_id) !== blockedUserId &&
            Number(user.connected_user_id) !== blockedUserId
        );

        state.sentRequests = state.sentRequests.filter(
          (user) =>
            Number(user.user_id) !== blockedUserId &&
            Number(user.receiver_user_id) !== blockedUserId
        );

        state.pendingRequests = state.pendingRequests.filter(
          (user) =>
            Number(user.user_id) !== blockedUserId &&
            Number(user.sender_user_id) !== blockedUserId
        );

        if (Number(state.selectedProfile?.user_id) === blockedUserId) {
          state.selectedProfile = null;
        }
      })
      .addCase(blockConnection.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ==========================
      // UNBLOCK USER
      // ==========================
      .addCase(unblockConnection.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(unblockConnection.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.message = action.payload?.message || "User unblocked";
      })
      .addCase(unblockConnection.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ==========================
      // MY CONNECTIONS
      // ==========================
      .addCase(fetchMyConnections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyConnections.fulfilled, (state, action) => {
        state.loading = false;
        state.myConnections = action.payload?.data || [];
      })
      .addCase(fetchMyConnections.rejected, (state, action) => {
        state.loading = false;
        state.myConnections = [];
        state.error = action.payload as string;
      })

      // ==========================
      // PENDING REQUESTS
      // ==========================
      .addCase(fetchPendingRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingRequests = action.payload?.data || [];
      })
      .addCase(fetchPendingRequests.rejected, (state, action) => {
        state.loading = false;
        state.pendingRequests = [];
        state.error = action.payload as string;
      })

      // ==========================
      // SENT REQUESTS
      // ==========================
      .addCase(fetchSentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSentRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.sentRequests = action.payload?.data || [];
        state.message = action.payload?.message || null;
      })
      .addCase(fetchSentRequests.rejected, (state, action) => {
        state.loading = false;
        state.sentRequests = [];
        state.error = action.payload as string;
      })

      // ==========================
      // CONNECTION STATUS
      // ==========================
      .addCase(fetchConnectionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConnectionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.connectionStatus = action.payload?.data || null;
      })
      .addCase(fetchConnectionStatus.rejected, (state, action) => {
        state.loading = false;
        state.connectionStatus = null;
        state.error = action.payload as string;
      })

      // ==========================
      // CONNECTED USER DETAILS
      // ==========================
      .addCase(fetchConnectedUserDetails.pending, (state) => {
        state.profileLoading = true;
        state.selectedProfile = null;
        state.error = null;
      })
      .addCase(fetchConnectedUserDetails.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.selectedProfile = action.payload?.data || null;
        state.message = action.payload?.message || null;
      })
      .addCase(fetchConnectedUserDetails.rejected, (state, action: any) => {
        state.profileLoading = false;
        state.selectedProfile = null;
        state.error =
          action.payload?.message ||
          action.payload ||
          "You can view details only for accepted connections";
      })

      // ==========================
      // COUNTS
      // ==========================
      .addCase(fetchUserNetworkCounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserNetworkCounts.fulfilled, (state, action) => {
        state.loading = false;
        state.counts = action.payload?.data || null;
      })
      .addCase(fetchUserNetworkCounts.rejected, (state, action) => {
        state.loading = false;
        state.counts = null;
        state.error = action.payload as string;
      });
  },
});

export const {
  resetConnectionError,
  resetConnectionMessage,
  setConnectionSearch,
  setConnectionPagination,
  clearConnectionStatus,
  resetSelectedProfile,
} = connectionSlice.actions;

export default connectionSlice.reducer;